<template>
  <div class="meta-boost">
    <!-- No boost created yet: the request form. -->
    <div v-if="!boost || boost.status === 'canceled'" class="ui form">
      <p v-if="boost && boost.status === 'canceled'" class="ui text grey small">
        Previous boost was canceled.
      </p>

      <div v-if="detecting" class="ui message">
        <p>
          <i class="ui active inline loader tiny" />
          Looking up this post on {{ platform === 'instagram' ? 'Instagram' : 'Facebook' }}&hellip;
        </p>
      </div>

      <!-- We already know which post this is (from the Facebook/Instagram post
           itself, an ad Meta already reports against it, or the background
           lookup above just found it) — never make the user hunt down Meta's
           internal post ID when we can resolve it for them. -->
      <div v-else-if="objectStoryId && !showManualEntry" class="ui text grey small margin-bottom-small">
        Boosting this post directly.
        <a href="#" @click.prevent="enterManualMode">Use a different post instead</a>
      </div>

      <div v-else-if="!showManualEntry" class="ui message">
        <p>
          We couldn't automatically identify this post on {{ platform === 'instagram' ? 'Instagram' : 'Facebook' }},
          so it can't be boosted yet.
          <a href="#" @click.prevent="showManualEntry = true">Enter the post ID manually</a>
          if you have it.
        </p>
      </div>

      <template v-if="!detecting && (objectStoryId || showManualEntry)">
        <div v-if="showManualEntry" class="field">
          <label v-if="platform === 'instagram'">Instagram Media ID</label>
          <label v-else>Object Story ID (Page ID_Post ID)</label>
          <input
            v-model="objectStoryId"
            type="text"
            :placeholder="platform === 'instagram' ? '18079736993534427' : '1234567890_9876543210'"
          />
          <p class="ui text grey small">
            Find this in Meta Ads Manager, under the post's identifiers &mdash; not
            something most people have on hand, so leave this blank unless you
            already have it.
          </p>
        </div>
        <div class="field">
          <label>Daily budget (USD)</label>
          <input v-model="dailyBudget" type="number" min="1" step="1" placeholder="10" />
        </div>
        <div v-if="error" class="ui negative message">
          <p>{{ error }}</p>
        </div>
        <button
          class="ui primary button"
          :class="{ loading: submitting }"
          :disabled="submitting || !objectStoryId || !dailyBudget"
          @click="createBoost"
        >
          <i class="icon bullhorn" /> Boost this post
        </button>
        <p class="ui text grey small margin-top-small">
          Creates a paused campaign/ad set/ad on Meta &mdash; nothing spends until you
          confirm and activate it below.
        </p>
      </template>
    </div>

    <!-- A boost exists: status + next action. -->
    <div v-else class="ui segment">
      <div class="ui small feed">
        <div class="event">
          <div class="content">
            <div class="summary">
              <span class="ui text bold">Object story:</span> {{ boost.object_story_id }}
              &mdash; ${{ boost.daily_budget }}/day
              <span v-if="boost.review_pending" class="ui orange basic label margin-left-small">
                <i class="icon clock outline" /> In review
              </span>
              <span v-else-if="boost.effective_status === 'DISAPPROVED'" class="ui red label margin-left-small">
                Disapproved by Meta
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="boost.status === 'pending' || boost.status === 'creating'" class="margin-top-small">
        <i class="ui active inline loader small" /> Creating paused campaign on Meta&hellip;
      </div>

      <div v-else-if="boost.status === 'created_paused' && !boost.spend_confirmed" class="margin-top-small">
        <div class="ui positive message">
          <p>Paused campaign, ad set, and ad created &mdash; nothing is spending yet.</p>
        </div>
        <div class="ui checkbox">
          <input id="spendConfirm" v-model="spendConfirmed" type="checkbox" />
          <label for="spendConfirm">I confirm I want to spend up to ${{ boost.daily_budget }}/day on this ad.</label>
        </div>
        <div class="margin-top-small">
          <button
            class="ui primary button"
            :class="{ loading: activating }"
            :disabled="!spendConfirmed || activating"
            @click="activate"
          >
            <i class="icon play" /> Activate (starts spending)
          </button>
          <button class="ui basic button" :disabled="canceling" @click="cancel">Cancel</button>
        </div>
      </div>

      <div v-else-if="boost.status === 'activating'" class="margin-top-small">
        <i class="ui active inline loader small" /> Activating&hellip;
      </div>

      <div v-else-if="boost.status === 'active'" class="margin-top-small">
        <span class="ui green label"><i class="icon play" /> Live</span>
        <span class="ui text grey small margin-left-small">Campaign {{ boost.campaign_id }} &middot; Ad {{ boost.ad_id }}</span>
        <div class="margin-top-small">
          <button class="ui basic button" :disabled="canceling" @click="cancel">Cancel</button>
        </div>
      </div>

      <div v-else-if="boost.status === 'failed'" class="ui negative message margin-top-small">
        <p>{{ boost.error_message || 'Something went wrong creating this boost.' }}</p>
      </div>
    </div>
  </div>
</template>

<script>
const POLL_STATUSES = ["pending", "creating", "activating"];
// A post's Meta-internal id (object_story_id / IG media id) is only known once
// a stats refresh has resolved it (Deliverable#default_object_story_id) — for a
// freshly-added deliverable that hasn't refreshed yet, that's often still nil.
// Rather than dead-ending straight to "paste the ID yourself", kick off the
// same refresh the rest of the app already uses and give it a few seconds to
// land before asking the user to do it by hand.
const DETECT_POLL_INTERVAL_MS = 3000;
const MAX_DETECT_ATTEMPTS = 10;

export default {
  props: {
    deliverableId: {
      type: Number,
      required: true,
    },
    initialBoost: {
      type: Object,
      default: null,
    },
    initialObjectStoryId: {
      type: String,
      default: "",
    },
    platform: {
      type: String,
      default: "facebook",
    },
  },
  data() {
    return {
      boost: this.initialBoost,
      objectStoryId: this.initialObjectStoryId || "",
      showManualEntry: false,
      dailyBudget: "",
      spendConfirmed: false,
      submitting: false,
      activating: false,
      canceling: false,
      detecting: false,
      detectAttempts: 0,
      error: null,
      pollTimer: null,
      detectTimer: null,
    };
  },
  mounted() {
    if (this.boost && (POLL_STATUSES.includes(this.boost.status) || this.boost.review_pending)) this.schedulePoll();
    if ((!this.boost || this.boost.status === "canceled") && !this.objectStoryId) this.detectObjectStoryId();
  },
  beforeUnmount() {
    clearTimeout(this.pollTimer);
    clearTimeout(this.detectTimer);
  },
  methods: {
    detectObjectStoryId() {
      this.detecting = true;
      this.detectAttempts = 0;
      // Fire-and-forget: same refresh_stats path the rest of the app already
      // uses (RefreshDeliverableStatsJob, high_priority queue). Its result
      // shows up via #default_object_story_id, which we poll for below.
      $.ajax({url: `/deliverables/${this.deliverableId}/refresh`, type: "GET", dataType: "json"});
      this.pollForObjectStoryId();
    },
    pollForObjectStoryId() {
      $.ajax({
        url: `/deliverables/${this.deliverableId}.json`,
        type: "GET",
        dataType: "json",
        success: (data) => {
          if (data.default_object_story_id) {
            this.objectStoryId = data.default_object_story_id;
            this.detecting = false;
            return;
          }
          this.detectAttempts += 1;
          if (this.detectAttempts >= MAX_DETECT_ATTEMPTS) {
            this.detecting = false;
            return;
          }
          this.detectTimer = setTimeout(() => this.pollForObjectStoryId(), DETECT_POLL_INTERVAL_MS);
        },
        error: () => {
          this.detecting = false;
        },
      });
    },
    csrfHeaders() {
      return {"X-CSRF-Token": $('meta[name="csrf-token"]').attr("content")};
    },
    enterManualMode() {
      this.showManualEntry = true;
      this.objectStoryId = "";
    },
    createBoost() {
      if (this.submitting) return;
      this.submitting = true;
      this.error = null;

      $.ajax({
        url: "/meta_boosts",
        type: "POST",
        dataType: "json",
        data: {
          deliverable_id: this.deliverableId,
          object_story_id: this.objectStoryId,
          daily_budget: this.dailyBudget,
          platform: this.platform,
        },
        headers: this.csrfHeaders(),
        success: (data) => {
          this.boost = data;
          this.schedulePoll();
        },
        error: (xhr) => {
          const resp = xhr.responseJSON;
          this.error = (resp && resp.error) || "Failed to create boost";
        },
        complete: () => {
          this.submitting = false;
        },
      });
    },
    activate() {
      if (this.activating || !this.boost) return;
      this.activating = true;

      $.ajax({
        url: `/meta_boosts/${this.boost.id}/activate`,
        type: "POST",
        dataType: "json",
        data: {spend_confirmed: this.spendConfirmed},
        headers: this.csrfHeaders(),
        success: (data) => {
          this.boost = data;
          this.schedulePoll();
        },
        error: (xhr) => {
          const resp = xhr.responseJSON;
          this.error = (resp && resp.error) || "Failed to activate boost";
        },
        complete: () => {
          this.activating = false;
        },
      });
    },
    cancel() {
      if (this.canceling || !this.boost) return;
      if (!confirm("Cancel this boost? It will stop spending.")) return;
      this.canceling = true;

      $.ajax({
        url: `/meta_boosts/${this.boost.id}/cancel`,
        type: "POST",
        dataType: "json",
        headers: this.csrfHeaders(),
        success: (data) => {
          this.boost = data;
        },
        error: (xhr) => {
          const resp = xhr.responseJSON;
          this.error = (resp && resp.error) || "Failed to cancel boost";
        },
        complete: () => {
          this.canceling = false;
        },
      });
    },
    schedulePoll() {
      clearTimeout(this.pollTimer);
      if (!this.boost) return;
      // Keep polling past our own lifecycle states (created_paused/active)
      // while Meta's ad review is still pending — review can resolve well
      // after we're done creating/activating, and the customer shouldn't
      // need to manually refresh to see it land.
      if (!POLL_STATUSES.includes(this.boost.status) && !this.boost.review_pending) return;
      const delay = POLL_STATUSES.includes(this.boost.status) ? 2000 : 15000;
      this.pollTimer = setTimeout(() => this.refresh(), delay);
    },
    refresh() {
      if (!this.boost) return;
      $.ajax({
        url: `/meta_boosts/${this.boost.id}`,
        type: "GET",
        dataType: "json",
        success: (data) => {
          this.boost = data;
          this.schedulePoll();
        },
      });
    },
  },
};
</script>
